import React from 'react';

import { Collapse, Table, type TableColumnsType } from 'antd-v5';
import { MdCloudDownload } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button, Text } from 'scouting-ui/lib';

import type { ImportFileRequirementRow } from '@appTypes/esb';

import { downloadImportErrorsCsv } from '../../utils';
import styles from './CampImportErrors.module.scss';

const PANEL_KEY = 'importErrors';

const fullName = (row: ImportFileRequirementRow) =>
  [row.firstName, row.middleName, row.lastName]
    .filter(part => !!part && part.trim())
    .join(' ');

const columns: TableColumnsType<ImportFileRequirementRow> = [
  {
    title: <FormattedMessage id="advancementImport.camp.errors.col.row" />,
    dataIndex: 'rowNumber',
    key: 'rowNumber',
    className: styles.rowCol,
  },
  {
    title: <FormattedMessage id="advancementImport.camp.errors.col.name" />,
    key: 'name',
    render: (_value, row) => fullName(row),
  },
  {
    title: (
      <FormattedMessage id="advancementImport.camp.errors.col.advancement" />
    ),
    key: 'advancement',
    render: (_value, row) => (
      <React.Fragment>
        <div>{row.advancement}</div>
        {!!row.mbVersion && (
          <Text font="body-overline">
            <span className={styles.muted}>{row.mbVersion}</span>
          </Text>
        )}
      </React.Fragment>
    ),
  },
  {
    title: <FormattedMessage id="advancementImport.camp.errors.col.errors" />,
    dataIndex: 'errors',
    key: 'errors',
    className: styles.errorsCol,
    render: (value: string) => value?.trim(),
  },
];

interface CampImportErrorsProps {
  rows: ImportFileRequirementRow[];
  /** From the summary report, so the count shows before the rows arrive */
  count: number;
  loading: boolean;
  /** Uploaded file name, used to name the exported CSV */
  fileName?: string | null;
}

const CampImportErrors: React.FC<CampImportErrorsProps> = props => {
  const { rows, count, loading, fileName } = props;

  const handleDownload = (event: React.MouseEvent) => {
    event.stopPropagation();
    downloadImportErrorsCsv(rows, fileName);
  };

  return (
    <Collapse
      className={styles.collapse}
      size="small"
      items={[
        {
          key: PANEL_KEY,
          label: (
            <Text font="body-caption">
              <FormattedMessage
                id="advancementImport.camp.errors.show"
                values={{ count }}
              />
            </Text>
          ),
          extra: (
            <Button
              type="default"
              size="small"
              icon={<MdCloudDownload />}
              disabled={!rows.length}
              onClick={handleDownload}
            >
              <FormattedMessage id="advancementImport.camp.errors.download" />
            </Button>
          ),
          children: (
            <Table<ImportFileRequirementRow>
              className={styles.table}
              rowKey="id"
              size="small"
              loading={loading}
              columns={columns}
              dataSource={rows}
              scroll={{ x: 'max-content' }}
              pagination={{
                pageSize: 10,
                size: 'small',
                hideOnSinglePage: true,
                showSizeChanger: false,
              }}
              locale={{
                emptyText: (
                  <FormattedMessage id="advancementImport.camp.errors.empty" />
                ),
              }}
            />
          ),
        },
      ]}
    />
  );
};

export default CampImportErrors;
